import { initializeApp, getApps } from 'firebase/app'
import { getMessaging, getToken, isSupported } from 'firebase/messaging'
import { useSessionStore } from '../../lib/Store/sessionStore.component';

const FALLBACK_FIREBASE_CONFIG = {
    apiKey: 'AIzaSyBRUtRt6hkLrLVEiQVsvFXatOSZa497PVY', // pragma: allowlist secret
    authDomain: 'esag-a8963.firebaseapp.com',
    projectId: 'esag-a8963',
    messagingSenderId: '1078918411760',
    appId: '1:1078918411760:web:d98426542cd943c2235935',
};

const FALLBACK_VAPID_KEY = 'BF_RaEwY85yMN-vZW2EOrBeMlW_GD9cL7-PnoD5wYgJn2ixfLFUcFI2BKQCmzAcZGhW_XlhZP9blYOCUDZoIjkE'; // pragma: allowlist secret

const wait = (delayMs: number) => new Promise((resolve) => setTimeout(resolve, delayMs));

const getServiceWorkerCandidates = (): string[] => {
    const candidates = new Set<string>();
    const publicUrl = (process.env.PUBLIC_URL || '').trim().replace(/\/$/, '');

    if (publicUrl) {
        candidates.add(`${publicUrl}/firebase-messaging-sw.js`);
    }

    if (typeof window !== 'undefined') {
        const fromCurrentPath = `${window.location.pathname.replace(/\/$/, '')}/firebase-messaging-sw.js`;
        candidates.add(fromCurrentPath.replace('//firebase-messaging-sw.js', '/firebase-messaging-sw.js'));
    }

    candidates.add('/firebase-messaging-sw.js');

    return Array.from(candidates);
};

const registerMessagingServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
    const serviceWorkerCandidates = getServiceWorkerCandidates();
    let lastError: unknown;

    for (const candidate of serviceWorkerCandidates) {
        try {
            return await navigator.serviceWorker.register(candidate);
        } catch (error) {
            lastError = error;
        }
    }

    throw lastError || new Error('Unable to register firebase messaging service worker');
};

const retrieveTokenWithRetry = async (
    messagingInstance: ReturnType<typeof getMessaging>,
    serviceWorkerRegistration: ServiceWorkerRegistration,
    vapidKey: string
): Promise<string | null> => {
    const maxAttempts = 3;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        const token = await getToken(messagingInstance, {
            vapidKey,
            serviceWorkerRegistration,
        });

        if (token) {
            return token;
        }

        if (attempt < maxAttempts) {
            await wait(800 * attempt);
        }
    }

    return null;
};

export default function initialize() {
    const { setNotificationToken } = useSessionStore.getState();
    const envFirebaseConfig = {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID,
    };
    const hasEnvFirebaseConfig = Object.values(envFirebaseConfig).every(Boolean);
    const firebaseConfig = hasEnvFirebaseConfig ? envFirebaseConfig : FALLBACK_FIREBASE_CONFIG;
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || FALLBACK_VAPID_KEY;

    if (
        typeof window === 'undefined' ||
        !('Notification' in window) ||
        !('serviceWorker' in navigator)
    ) {
        return;
    }

    isSupported()
        .then((supported) => {
            if (!supported) return;

            const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

            const messagingInstance = getMessaging(app);

            const getAndStoreToken = async () => {
                try {
                    const serviceWorkerRegistration = await registerMessagingServiceWorker();
                    const token = await retrieveTokenWithRetry(messagingInstance, serviceWorkerRegistration, vapidKey);

                    if (token) {
                        setNotificationToken(token);
                    } else {
                        console.warn('[Notifications][web] FCM token unavailable after retries');
                    }
                } catch (error) {
                    console.error('[Notifications][web] Failed to retrieve FCM token', error);
                }
            };

            if(Notification.permission === 'granted'){
                getAndStoreToken().catch(() => undefined);
            } else if (Notification.permission !== 'denied') {
                Notification.requestPermission()
                    .then(async (permission) => {
                        if (permission !== 'granted') return;

                        await getAndStoreToken();
                    })
                    .catch(() => undefined);
            }

        })
        .catch(() => undefined);
}
